import {
  ActionListDataItem,
  BannerWrapper,
  Divider,
  StackedList,
  Typography,
  Alert,
} from '@ecdlink/ui';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useAppDispatch } from '@store';
import { userSelectors } from '@store/user';
import { analyticsActions } from '@store/analytics';
import * as styles from './add-expense.styles';
import ROUTES from '@routes/routes';
import Rent from './components/rent/rent';
import Utilities from './components/utilities/utilities';
import SalaryAndWages from './components/salary-and-wages/salaray-and-wages';
import Food from './components/food/food';
import LearningMaterials from './components/learning-materials/learning-materials';
import AnnualMaintenance from './components/annual-maintenance/annual-maintenance';
import OtherExpense from './components/other-expense/other';
import { StatementsExpensesInput } from '@ecdlink/graphql';
import {
  statementsSelectors,
  statementsThunkActions,
} from '@/store/statements';
import { authSelectors } from '@/store/auth';
import { AddExpenseItemState, expenseTypes } from './add-expense.types';

export const AddExpense: React.FC = () => {
  const userAuth = useSelector(authSelectors.getAuthUser);
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const location = useLocation<AddExpenseItemState>();
  const expenseTypesItems = useSelector(statementsSelectors.getExpensesTypes);
  const toBeEditedExpenseItem = location?.state?.expenseItem;

  useEffect(() => {
    if (!isOnline) {
      appDispatch(
        analyticsActions.createViewTracking({
          pageView: window.location.pathname,
          title: 'Practitioner About',
        })
      );
    }
  }, [appDispatch, isOnline]);

  const user = useSelector(userSelectors.getUser);
  const [listItems, setListItems] = useState<ActionListDataItem[]>([]);
  const [type, setType] = useState('');

  useEffect(() => {
    if (user) {
      setNewStackListItems();
    }
  }, [user]);

  useEffect(() => {
    if (location.state?.isEdit) {
      const expenseTypeDescription = expenseTypesItems?.find(
        (type) => type.id === toBeEditedExpenseItem?.expenseTypeId
      )?.description;
      setType(expenseTypeDescription || '');
    }
  }, [
    expenseTypesItems,
    toBeEditedExpenseItem?.expenseTypeId,
    location?.state,
  ]);

  const onSubmit = useCallback(
    async (expenseItem: StatementsExpensesInput) => {
      await appDispatch(
        statementsThunkActions.addExpenseItem({
          input: expenseItem,
          firstAttempt: true,
        })
      );

      await appDispatch(
        statementsThunkActions.getUnsubmittedExpenseItems({})
      ).unwrap();
    },
    [userAuth]
  );

  const incomeType = (type?: string) => {
    switch (type) {
      case expenseTypes.rent:
        return (
          <Rent
            setType={setType}
            onSubmit={onSubmit}
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.utilities:
        return (
          <Utilities
            setType={setType}
            onSubmit={onSubmit}
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.subcontractorWages:
        return (
          <SalaryAndWages
            setType={setType}
            onSubmit={onSubmit}
            title="Wages Paid to My Staff"
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.principalWages:
        return (
          <SalaryAndWages
            setType={setType}
            onSubmit={onSubmit}
            title="My Salary"
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.food:
        return (
          <Food
            setType={setType}
            onSubmit={onSubmit}
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.learningMaterials:
        return (
          <LearningMaterials
            setType={setType}
            onSubmit={onSubmit}
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.maintenance:
        return (
          <AnnualMaintenance
            setType={setType}
            onSubmit={onSubmit}
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      case expenseTypes.other:
        return (
          <OtherExpense
            setType={setType}
            onSubmit={onSubmit}
            expenseItem={toBeEditedExpenseItem}
            isEdit={location.state?.isEdit}
          />
        );
      default:
        break;
    }
  };

  const setNewStackListItems = () => {
    const list: ActionListDataItem[] = [
      {
        title: 'Rent',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Cost for using programme venue',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.rent),
      },
      {
        title: 'Utilities (electricity, water & other running costs)',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Incl. airtime, data, insurance',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.utilities),
      },
      {
        title: 'My Salary',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Incl. stipend if used for salary',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.principalWages),
      },
      {
        title: 'Wages Paid to My Staff',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'All money paid to staff/assistants ',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.subcontractorWages),
      },
      {
        title: 'Food',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Programme meals & snacks',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.food),
      },
      {
        title: 'Learning materials',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Books, toys, copying, etc.',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.learningMaterials),
      },
      {
        title: 'Annual maintenance & purchases',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Paint for building, new gate, etc.',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.maintenance),
      },
      {
        title: 'Other',
        titleStyle: 'text-textDark font-semibold',
        subTitle: 'Add your own expense type',
        subTitleStyle: 'text-textMid',
        actionName: 'Add',
        actionIcon: 'PlusIcon',
        buttonType: 'filled',
        onActionClick: () => setType(expenseTypes.other),
      },
    ];

    setListItems(list);
  };

  return (
    <div className={styles.container}>
      {type ? (
        <div>{incomeType(type)}</div>
      ) : (
        <BannerWrapper
          showBackground={false}
          title={'Add expense (money out)'}
          color={'primary'}
          size="medium"
          renderBorder={true}
          renderOverflow={false}
          onBack={() => history.push(ROUTES.BUSINESS_ADD_AMOUNT)}
          displayOffline={!isOnline}
          className="p-4"
        >
          <div>
            <Typography type="h3" color={'textDark'} text={'Add an expense'} />
            <Typography
              type="body"
              color={'textMid'}
              text={'What did you pay for?'}
            />
          </div>
          <Divider dividerType="dashed" className="mt-4" />
          <div>
            <StackedList
              className={'h-auto'}
              listItems={listItems}
              type={'ActionList'}
            ></StackedList>
          </div>
          <Alert
            type={'info'}
            title={
              "If you don't see the expense type you want to add above, use the “Other” type to add your own."
            }
            list={[
              'For example: cost of training for staff, transport, or other items.',
            ]}
            className="mt-6"
          />
        </BannerWrapper>
      )}
    </div>
  );
};
